// ============================================================================
// BESPOKIAN Health - Shopping Cart Page
// Cart view with medication list and checkout CTA
// ============================================================================

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Trash2, ShoppingBag, ArrowRight, Pill, Truck, Shield, Clock } from 'lucide-react';

interface CartItem {
  id: string;
  name: string;
  genericName?: string;
  dosage: string;
  form: string;
  quantity: number;
  priceCents: number;
  retailPriceCents?: number;
}

// Demo cart items
const demoItems: CartItem[] = [
  {
    id: '1',
    name: 'Lisinopril',
    genericName: 'Lisinopril',
    dosage: '10mg',
    form: 'Tablets',
    quantity: 90,
    priceCents: 1247,
    retailPriceCents: 4500,
  },
  {
    id: '2',
    name: 'Metformin',
    genericName: 'Metformin HCl',
    dosage: '500mg',
    form: 'Tablets',
    quantity: 60,
    priceCents: 987,
    retailPriceCents: 3200,
  },
];

export function CartPage() {
  const [items, setItems] = useState<CartItem[]>(demoItems);

  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const subtotalCents = items.reduce((sum, item) => sum + item.priceCents, 0);
  const shippingCents = subtotalCents >= 5000 ? 0 : 599;
  const totalCents = subtotalCents + shippingCents;

  const totalRetailCents = items.reduce((sum, item) => sum + (item.retailPriceCents || item.priceCents), 0);
  const savingsCents = totalRetailCents - subtotalCents;

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1 pt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <div className="mb-8">
            <div className="w-12 h-0.5 bg-gold-500 mb-4" />
            <h1 className="font-display text-3xl text-navy-800 tracking-tight italic">Your Cart</h1>
            <p className="text-navy-500 mt-1">{items.length} medication{items.length !== 1 ? 's' : ''}</p>
          </div>

          {items.length === 0 ? (
            /* Empty Cart */
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="h-10 w-10 text-navy-400" />
              </div>
              <h2 className="font-display text-2xl text-navy-800 tracking-tight mb-2">Your cart is empty</h2>
              <p className="text-navy-500 mb-8">Add medications to get started with transparent pricing.</p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold rounded-lg transition-colors"
              >
                Browse Medications <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border border-navy-100 p-5 hover:shadow-card transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-sage-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Pill className="h-7 w-7 text-sage-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-navy-800">{item.name}</h3>
                            {item.genericName && (
                              <p className="text-sm text-navy-500">({item.genericName})</p>
                            )}
                            <p className="text-sm text-navy-600 mt-1">
                              {item.dosage} • {item.form} • Qty {item.quantity}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-display text-xl text-navy-800 tracking-tight">{formatPrice(item.priceCents)}</p>
                            {item.retailPriceCents && (
                              <p className="text-sm text-navy-400 line-through">
                                {formatPrice(item.retailPriceCents)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-4">
                          <span className="px-2 py-1 bg-sage-50 text-sage-700 text-xs font-medium rounded-full">
                            Prescription Required
                          </span>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="flex items-center gap-1 text-sm text-coral-600 hover:text-coral-700"
                          >
                            <Trash2 className="h-4 w-4" /> Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Benefits */}
                <div className="grid sm:grid-cols-3 gap-4 mt-6">
                  <div className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                    <Truck className="h-5 w-5 text-gold-600" />
                    <span className="text-sm text-navy-700">Free shipping on $50+</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                    <Shield className="h-5 w-5 text-sage-600" />
                    <span className="text-sm text-navy-700">HIPAA compliant</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-navy-50 rounded-lg">
                    <Clock className="h-5 w-5 text-navy-500" />
                    <span className="text-sm text-navy-700">2-3 day delivery</span>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-navy-50 rounded-xl p-6 border border-navy-100 sticky top-32">
                  <h2 className="font-display text-lg text-navy-800 tracking-tight mb-4">Order Summary</h2>

                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between text-navy-600">
                      <span>Subtotal</span>
                      <span>{formatPrice(subtotalCents)}</span>
                    </div>
                    <div className="flex justify-between text-navy-600">
                      <span>Shipping</span>
                      <span>{shippingCents === 0 ? 'FREE' : formatPrice(shippingCents)}</span>
                    </div>
                    {savingsCents > 0 && (
                      <div className="flex justify-between text-sage-600 font-medium">
                        <span>You Save</span>
                        <span>-{formatPrice(savingsCents)}</span>
                      </div>
                    )}
                    <div className="border-t border-navy-200 pt-3 mt-3">
                      <div className="flex justify-between text-navy-800 font-semibold text-base">
                        <span>Total</span>
                        <span className="font-serif text-xl">{formatPrice(totalCents)}</span>
                      </div>
                    </div>
                  </div>

                  {shippingCents > 0 && (
                    <p className="text-xs text-navy-500 mt-3">
                      Add {formatPrice(5000 - subtotalCents)} more for free shipping
                    </p>
                  )}

                  <Link
                    to="/checkout"
                    className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-gold-500 hover:bg-gold-400 text-navy-800 font-semibold rounded-lg transition-colors"
                  >
                    Proceed to Checkout <ArrowRight className="h-4 w-4" />
                  </Link>

                  <p className="text-xs text-navy-400 text-center mt-4">
                    Every prescription reviewed by a licensed pharmacist
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
